import { useState } from 'react'
import { useRecoilState } from 'recoil'
import { ContentView } from '../../../api/client'
import { isUserAdminState } from '../../../store/DomainStore'
import { getClassifiersToDisplay } from '../../../util/classifierListUtils'
import ClassificationDisplay from './ClassificationDisplay'
import ShowHideChevronToggle from './ShowHideChevronToggle'

type Props = {
    content: ContentView
}

/**
 * Shows a list of the content classifications.
 *
 * By default this only shows classifications that have contributed to the
 * current moderation. So it shows only the classifications that are relevant
 * to the moderation.
 *
 * If the user is an admin it will also show a "Show All" button to show
 * all the other classifications that are above a certain figure (0.2/LOW I think)
 */
export default function ContentClassificationList(props: Props) {
    const { content } = props
    const [showAll, setShowAll] = useState(false)

    const classifiersToDisplay = getClassifiersToDisplay(content)

    // Is the user an admin?
    const [admin] = useRecoilState(isUserAdminState)

    // Only what put the comment in a queue is shown: a severe or a suspect
    // verdict. Everything else sits behind the admin caret, so the card says why
    // this comment needs attention rather than listing everything that ran on it.
    // — Claude
    const restOfClassifications = classifiersToDisplay.safeClassifications

    if (!content) {
        return <></>
    }

    function handleShowAllOnClick() {
        setShowAll(!showAll)
    }

    return (
        // `min-w-0` so this list can be narrower than its widest line and let
        // the pills wrap; a flex item defaults to `min-width: auto`, which would
        // push the row wider instead. — Claude
        <div className="flex min-w-0 flex-row flex-wrap items-center gap-3 gap-y-3">
            {classifiersToDisplay.relevantSevereClassifications.map((classification) => (
                <span
                    key={`${classification.classifierName}_${classification.classification}_${content.account.id}_${content.id}`}
                >
                    <ClassificationDisplay
                        queue="SEVERE"
                        classification={classification}
                        accountId={content.account.id}
                        contentId={content.id}
                    />
                </span>
            ))}
            {classifiersToDisplay.relevantSuspectClassifications.map((classification) => (
                <span
                    key={`${classification.classifierName}_${classification.classification}_${content.account.id}_${content.id}`}
                >
                    <ClassificationDisplay
                        queue="SUSPECT"
                        classification={classification}
                        accountId={content.account.id}
                        contentId={content.id}
                    />
                </span>
            ))}

            {admin && restOfClassifications.length > 0 && (
                <ShowHideChevronToggle showAll={showAll} onShowAllChange={handleShowAllOnClick} />
            )}

            {showAll &&
                restOfClassifications.map((classification) => {
                    return (
                        <span
                            key={`${classification.classifierName}_${classification.classification}_${content.account.id}_${content.id}`}
                        >
                            <ClassificationDisplay
                                queue="SAFE"
                                classification={classification}
                                accountId={content.account.id}
                                contentId={content.id}
                            />
                        </span>
                    )
                })}
        </div>
    )
}
